import { useState } from 'react'
import { Button } from './ui/Button'
import { Dialog } from './ui/Dialog'
import { ConfirmDialog } from './ui/ConfirmDialog'
import { Field, Select } from './ui/Field'
import { DateInput, TimeInput } from './ui/DateTimeInputs'
import { Badge } from './ui/Badge'
import { useT } from '../i18n/context'
import { errText } from '../lib/errors'
import {
    formatHours,
    formatTime,
    fromLocalInputValue,
    todayLocalInputValue,
} from '../lib/format'
import { toast } from '../stores/toast'
import { useEntryMutations, useTimeEntries } from '../hooks/useApi'
import { useWorkingTimeMonthly } from '../hooks/useWorkingTimeApi'
import { ABSENCE_TYPES, entryTypeMeta } from '../lib/labelMaps'
import type { TimeEntry } from '../types/api'
import type { DictPath } from '../i18n/translate'

export type AbsenceType =
    | 'SICK'
    | 'VACATION'
    | 'TIME_OFF'
    | 'PAID_ABSENCE'

const typeKeys: Record<AbsenceType, DictPath> = {
    SICK: 'entryType.sick',
    VACATION: 'entryType.vacation',
    TIME_OFF: 'entryType.timeOff',
    PAID_ABSENCE: 'entryType.paidAbsence',
}

/**
 * Buchungs-Modi (Owner-Regeln 08.09.): SICK/VACATION ausschließlich
 * halb oder ganz; TIME_OFF/PAID_ABSENCE stundenweise — stundenweise
 * Abwesenheiten dürfen das Ist nie über das Soll heben.
 */
type AbsenceMode = 'full' | 'half' | 'hours'

function allowedModes(type: AbsenceType): AbsenceMode[] {
    if (type === 'SICK' || type === 'VACATION') return ['full', 'half']
    return ['hours']
}

function defaultMode(type: AbsenceType): AbsenceMode {
    return allowedModes(type)[0]
}

/**
 * Abwesenheit eintragen (Konzept F3/4.4, Regeln 08.09.): dialoggeführt
 * pro Tag — zeigt bestehende Abwesenheiten des Tages (inkl. Löschen),
 * „ganztägig" trägt das Tages-Soll, „halber Tag" die Hälfte davon.
 * OWNER/ADMIN können über userId für andere Mitglieder eintragen.
 */
export function AbsenceDialog({
    open,
    onClose,
    workspaceId,
    userId,
    initialDate,
    initialType = 'SICK',
}: {
    open: boolean
    onClose: () => void
    workspaceId: number
    /** Ziel-Mitglied (nur OWNER/ADMIN für fremde; undefined = selbst) */
    userId?: number
    initialDate?: string
    initialType?: AbsenceType
}) {
    const t = useT()
    const { create, remove } = useEntryMutations(workspaceId)

    const today = todayLocalInputValue()
    const initDate = initialDate ?? today
    const [initializedFor, setInitializedFor] = useState<string | null>(null)
    const [type, setType] = useState<AbsenceType>(initialType)
    const [date, setDate] = useState(initDate)
    const [mode, setMode] = useState<AbsenceMode>(defaultMode(initialType))
    const [fromTime, setFromTime] = useState('09:00')
    const [toTime, setToTime] = useState('17:00')
    const [deleting, setDeleting] = useState<TimeEntry | null>(null)

    // Reset synchron im Render (initializedFor-Muster), Key = Öffnen-Event
    const openKey = open ? `${initDate}:${initialType}:${userId ?? 'self'}` : null
    if (openKey !== null && initializedFor !== openKey) {
        setInitializedFor(openKey)
        setType(initialType)
        setDate(initDate)
        setMode(defaultMode(initialType))
        setFromTime('09:00')
        setToTime('17:00')
        setDeleting(null)
    }
    // Typwechsel innerhalb des Dialogs: Modus auf erlaubte Defaults zurück
    if (!allowedModes(type).includes(mode)) {
        setMode(defaultMode(type))
    }

    // Tages-Soll aus der Monats-Auswertung (Cache-treu). Bei halb
    // getipptem Datum den letzten vollständigen Stand behalten, damit
    // der Query-Key stabil bleibt.
    const monthKey = /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : initDate
    const monthDate = new Date(`${monthKey}T12:00:00`)
    const monthly = useWorkingTimeMonthly(
        workspaceId,
        userId ?? null,
        monthDate.getFullYear(),
        monthDate.getMonth() + 1,
    )
    const member = monthly.data?.member
    const settings = monthly.data?.settings
    const dayRow = monthly.data?.weeks
        .flatMap(w => w.days)
        .find(d => d.date === monthKey)
    const dailyTargetSeconds =
        member?.weeklyTargetHours != null && settings
            ? Math.round(
                  (member.weeklyTargetHours * 3600) / settings.workdays.length,
              )
            : null
    const halfDaySeconds =
        dailyTargetSeconds !== null
            ? Math.round(dailyTargetSeconds / 2 / 60) * 60
            : null

    // Bestehende Abwesenheiten des Tages (löschen statt nur über Einträge)
    const dayEntries = useTimeEntries(workspaceId, {
        ...(userId !== undefined ? { userId } : {}),
        startDate: fromLocalInputValue(`${monthKey}T00:00`),
        endDate: fromLocalInputValue(`${monthKey}T23:59`),
        limit: 100,
    })
    const existingAbsences = (dayEntries.data?.data ?? []).filter(e =>
        ABSENCE_TYPES.has(e.entryType),
    )

    // Stundenweise Abwesenheiten dürfen das Ist nie über das Soll heben:
    // gebucht = Arbeit + bezahlte Abw. (im Ist enthalten) + bisheriger
    // Zeitausgleich + schon gedecktes Soll durch Krank/Urlaub.
    // Feiertag/Arbeitstag über die Tageszeile.
    const targetSeconds = dayRow?.targetSeconds ?? dailyTargetSeconds
    const remainingSeconds =
        targetSeconds !== null && targetSeconds !== undefined
            ? Math.max(
                  0,
                  targetSeconds -
                      (dayRow?.workedSeconds ?? 0) -
                      (dayRow?.timeOffSeconds ?? 0) -
                      (dayRow?.sickSeconds ?? 0) -
                      (dayRow?.vacationSeconds ?? 0),
              )
            : null

    const changeType = (next: AbsenceType) => {
        setType(next)
        setMode(defaultMode(next))
    }

    const formatEnd = (start: Date, seconds: number) => {
        const end = new Date(start.getTime() + seconds * 1000)
        const pad = (n: number) => String(n).padStart(2, '0')
        return `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}T${pad(end.getHours())}:${pad(end.getMinutes())}`
    }

    const submit = (e: React.FormEvent) => {
        e.preventDefault()
        let startLocal: string
        let endLocal: string
        if (mode === 'full' || mode === 'half') {
            if (dailyTargetSeconds === null) {
                toast.error(t('workingTime.absenceFullDayNoSetup'))
                return
            }
            const start = new Date(`${date}T00:00`)
            startLocal = `${date}T00:00`
            endLocal = formatEnd(
                start,
                mode === 'full' ? dailyTargetSeconds : (halfDaySeconds ?? 0),
            )
        } else {
            if (!fromTime || !toTime || toTime <= fromTime) {
                toast.error(t('workingTime.absenceTimeInvalid'))
                return
            }
            const [fh, fm] = fromTime.split(':').map(Number)
            const [th, tm] = toTime.split(':').map(Number)
            const requestedSeconds =
                (th * 60 + tm - (fh * 60 + fm)) * 60
            if (
                remainingSeconds !== null &&
                requestedSeconds > remainingSeconds
            ) {
                toast.error(
                    t('workingTime.absenceExceedsTarget', {
                        hours: formatHours(remainingSeconds),
                    }),
                )
                return
            }
            startLocal = `${date}T${fromTime}`
            endLocal = `${date}T${toTime}`
        }
        create.mutate(
            {
                description: '',
                entryType: type,
                isBillable: false,
                startTime: fromLocalInputValue(startLocal),
                endTime: fromLocalInputValue(endLocal),
                ...(userId !== undefined ? { userId } : {}),
            },
            {
                onSuccess: () => {
                    toast.success(t('workingTime.absenceSaved'))
                    onClose()
                },
                onError: err => toast.error(errText(err)),
            },
        )
    }

    const modes = allowedModes(type)
    const hours = halfDaySeconds !== null ? halfDaySeconds / 3600 : null

    return (
        <Dialog open={open} onClose={onClose} title={t('workingTime.absenceTitle')}>
            <form onSubmit={submit} className='space-y-4'>
                {existingAbsences.length > 0 && (
                    <div>
                        <p className='text-xs font-semibold tracking-wide text-slate-500 dark:text-slate-400 uppercase'>
                            {t('workingTime.absenceExisting')}
                        </p>
                        <ul className='mt-1 divide-y divide-slate-100 dark:divide-slate-800'>
                            {existingAbsences.map(e => (
                                <li
                                    key={e.id}
                                    className='flex items-center justify-between gap-2 py-1.5'
                                >
                                    <span className='flex min-w-0 items-center gap-2 text-sm text-slate-600 dark:text-slate-300'>
                                        <Badge
                                            tone={
                                                entryTypeMeta[e.entryType].tone
                                            }
                                        >
                                            {t(
                                                entryTypeMeta[e.entryType]
                                                    .labelKey,
                                            )}
                                        </Badge>
                                        <span className='tabular-nums'>
                                            {formatTime(e.startTime)}
                                            {e.endTime
                                                ? `–${formatTime(e.endTime)}`
                                                : ''}
                                        </span>
                                    </span>
                                    <Button
                                        type='button'
                                        variant='ghost'
                                        size='sm'
                                        className='text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/60'
                                        onClick={() => setDeleting(e)}
                                    >
                                        {t('common.delete')}
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <Field label={t('workingTime.absenceType')}>
                    <Select value={type} onChange={e => changeType(e.target.value as AbsenceType)}>
                        {(Object.keys(typeKeys) as AbsenceType[]).map(id => (
                            <option key={id} value={id}>
                                {t(entryTypeMeta[id].labelKey)}
                            </option>
                        ))}
                    </Select>
                </Field>
                <Field label={t('workingTime.absenceDate')}>
                    <DateInput value={date} onChange={setDate} />
                </Field>

                {/* Modus: Krank/Urlaub halb oder ganz, ZA/bezahlte Abw.
                    stundenweise (keine Modus-Wahl nötig) */}
                {modes.includes('half') && (
                    <div className='flex flex-wrap gap-2'>
                        {(['full', 'half'] as const).map(m => (
                            <Button
                                key={m}
                                type='button'
                                variant={mode === m ? 'primary' : 'secondary'}
                                size='sm'
                                onClick={() => setMode(m)}
                                disabled={dailyTargetSeconds === null}
                            >
                                {m === 'full'
                                    ? t('workingTime.absenceFullDay')
                                    : t('workingTime.absenceHalfDay')}
                            </Button>
                        ))}
                    </div>
                )}
                {mode === 'full' && dailyTargetSeconds !== null && (
                    <p className='text-xs text-slate-500 dark:text-slate-400'>
                        {t('workingTime.absenceFullDayHint', {
                            hours: formatHours(dailyTargetSeconds),
                        })}
                    </p>
                )}
                {mode === 'half' && dailyTargetSeconds !== null && (
                    <p className='text-xs text-slate-500 dark:text-slate-400'>
                        {t(
                            type === 'VACATION'
                                ? 'workingTime.absenceHalfDayHint'
                                : 'workingTime.absenceHalfDayPlainHint',
                            {
                                hours: (hours ?? 0).toLocaleString(undefined, {
                                    maximumFractionDigits: 2,
                                }),
                            },
                        )}
                    </p>
                )}
                {(mode === 'full' || mode === 'half') &&
                    dailyTargetSeconds === null && (
                        <p className='text-xs text-slate-500 dark:text-slate-400'>
                            {t('workingTime.absenceFullDayNoSetup')}
                        </p>
                    )}
                {mode === 'hours' && (
                    <>
                        <div className='grid grid-cols-2 gap-3'>
                            <Field label={t('workingTime.absenceFrom')}>
                                <TimeInput
                                    value={fromTime}
                                    onChange={setFromTime}
                                />
                            </Field>
                            <Field label={t('workingTime.absenceTo')}>
                                <TimeInput
                                    value={toTime}
                                    onChange={setToTime}
                                />
                            </Field>
                        </div>
                        {remainingSeconds !== null && (
                            <p className='text-xs text-slate-500 dark:text-slate-400'>
                                {t('workingTime.absenceRemaining', {
                                    hours: formatHours(remainingSeconds),
                                })}
                            </p>
                        )}
                    </>
                )}
                <p className='text-xs text-slate-500 dark:text-slate-400'>
                    {type === 'PAID_ABSENCE'
                        ? t('workingTime.absencePaidHint')
                        : type === 'VACATION'
                          ? t('workingTime.absenceVacationHint')
                          : t('workingTime.absenceSickHint')}
                </p>
                <div className='flex justify-end gap-2'>
                    <Button type='button' variant='secondary' onClick={onClose}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type='submit'
                        loading={create.isPending}
                        disabled={!date}
                    >
                        {t('workingTime.absenceSave')}
                    </Button>
                </div>
            </form>
            <ConfirmDialog
                open={deleting !== null}
                message={t('entries.deleteConfirm')}
                confirmLabel={t('common.delete')}
                loading={remove.isPending}
                onClose={() => setDeleting(null)}
                onConfirm={() => {
                    if (!deleting) return
                    remove.mutate(deleting.id, {
                        onSuccess: () => {
                            toast.success(t('workingTime.absenceDeleted'))
                            setDeleting(null)
                        },
                        onError: err => toast.error(errText(err)),
                    })
                }}
            />
        </Dialog>
    )
}
